<template>
  <div
    v-if="isVisible"
    class="section"
  >
    <div class="container max-width-mobile">
      <template v-if="currentUser.accountId && loyalty.isEnrolled">
        <loyalty-summary-card
          class="section__child padding"
          :hide-learn-more="true"
          :use-card-style="false"
        />

        <div class="section__child">
          <div class="layout layout--horizontal">
            <p
              v-if="isEmailAndPasswordAuthType"
              class="dark"
            >
              {{ currentUser.email }}
            </p>
            <p
              v-else
              class="dark"
            >
              {{ $phone(currentUser.primarySmsNumber) }}
            </p>

            <button
              type="button"
              class="link link--margin primary smaller uppercase"
              @click="signOutClickHandler"
            >
              Sign Out
            </button>
          </div>
        </div>
      </template>

      <validated-form
        v-else
        name="loyaltyPartialSignInForm"
        @valid-submit="formSubmitHandler"
      >
        <div class="center">
          <h3 class="title title--4 bold primary half-spacing">{{ loyaltyContent.metadata.title }}</h3>

          <p>
            {{ loyaltyContent.metadata.subtitle }}

            <button
              v-if="content.PayAtTableLoyaltyProgramDetails.metadata.programDetails"
              class="link dark"
              type="button"
              @click="loyaltyProgramDetailsClickHandler"
            >
              Learn More
            </button>
          </p>
        </div>

        <template v-if="isEmailAndPasswordAuthType">
          <text-input
            v-model="email"
            type="email"
            label="Email"
            name="loyalty-email"
            :validations="{ required: true }"
          />

          <text-input
            v-model="password"
            type="password"
            label="Password"
            name="password"
            :validations="{ required: true }"
          />

          <button
            class="button button--secondary button--large"
            :class="{ 'button--loading': isSigningIn }"
            type="submit"
            :disabled="!email || !password"
          >
            {{ loyaltyContent.metadata.buttonCta }}
          </button>
        </template>

        <div
          v-else
          class="input-with-button input-with-button--merge"
        >
          <div class="input-with-button__input">
            <text-input
              v-model="phoneNumber"
              type="phone"
              label="Phone Number"
              name="loyaltyPhoneNumber"
              :validations="{ required: true }"
            />
          </div>

          <div class="input-with-button__button">
            <button
              :class="['button button--secondary button--large', { 'button--loading': isSigningIn }]"
              type="submit"
              :disabled="!phoneNumber"
            >
              {{ loyaltyContent.metadata.buttonCta }}
            </button>
          </div>
        </div>

        <checkbox-input
          v-if="content.PayAtTableLoyaltyProgramDetails.metadata.termsAndConditions"
          v-model="termsAccepted"
          label="Terms of Use"
          name="loyaltyTermsAccepted"
          :validations="{ required: true }"
        >
          I agree to the

          <button
            class="link"
            type="button"
            @click="loyaltyTermsAndConditionsClickHandler"
          >
            Terms & Conditions
          </button>
        </checkbox-input>
      </validated-form>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';

  import parseTwoFactorError from '@/helpers/parse-two-factor-error';


  export default {
    name: 'LoyaltyPartialSignIn',

    props: {
      receipt: {
        type: Object,
        default: null
      }
    },

    data() {
      return {
        isResendingCode: false,
        isSigningIn: false,
        isVerifying: false,
        phoneNumber: undefined,
        email: undefined,
        password: undefined,
        termsAccepted: true,
        verificationCode: null
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('merchant', ['loyaltyOptions']),
      ...mapGetters('orders', ['order']),
      ...mapGetters('offers', ['loyalty']),
      ...mapGetters('session', ['currentUser']),
      ...mapGetters('socket', ['isPrimaryOrderRoomUser']),

      isVisible() {
        if (this.receipt) {
          return this.loyaltyOptions.payAtTable.isVisible
            && !this.currentUser.accountId
            && !this.receipt.loyaltyEngagementSummary;
        }

        return this.loyaltyOptions.payAtTable.isVisible
          && this.isPrimaryOrderRoomUser
          && (this.order.orderAttributes.canAssignAccount || !!this.currentUser.accountId);
      },

      loyaltyContent() {
        return this.receipt ? this.content.PayAtTableReceiptLoyaltyOffer : this.content.PayAtTableCheckoutLoyaltyOffer;
      },

      isEmailAndPasswordAuthType() {
        return this.loyaltyOptions.payAtTable.payAtTableAuthenticationType === 'EmailAndPasswordLogin';
      }
    },

    methods: {
      /** NOTE:
       * If payAtTableAuthenticationType === 'EmailAndPasswordLogin then and ONLY will a 3rd party provider (like Punnch) handle the enrollment.
       * Payload must be 'externalPassword: password'
       *
       * If payAtTableAuthenticationType === 'PhoneNumberLogin', then and ONLY CardFree loyalty provider will handle the enrollment
       */

      async formSubmitHandler() {
        try {
          this.isSigningIn = true;

          const dispatchPayload = { phoneNumber: this.phoneNumber, email: this.email, externalPassword: this.password };

          await this.$store.dispatch('session/signInViaDevice', dispatchPayload);

          this.handleSuccess();
        }

        catch (error) {
          this.handleError(error);
        }

        finally {
          this.isSigningIn = false;
        }
      },

      handleError(error) {
        const { requiresAuth, ...rest } = parseTwoFactorError(error, this.email, this.phoneNumber);

        if (requiresAuth) {
          const twoFactorModalPayload = { ...rest };

          this.$openModal('twoFactorAuthModal', twoFactorModalPayload);
        }

        else if (this.isEmailAndPasswordAuthType) {
          /* might be worth having CAPI respond with a specific error code in this case */
          const notificationEventPayload = {
            type: 'error',
            message: this.loyaltyContent.metadata.loginFailureMessage,
            url: this.loyaltyContent.metadata.loginFailureActionLink,
            buttonText: this.loyaltyContent.metadata.loginFailureActionText,
            clickToDismiss: true
          };

          events.emit('notification', notificationEventPayload);
        }

        else {
          errorHandlers.generic(error);
        }
      },

      handleSuccess() {
        const notificationEventPayload = {
          type: 'success',
          message: this.loyaltyContent.metadata.enrollmentSuccessMessage,
          url: this.loyaltyContent.metadata?.enrollmentSuccessActionLink,
          buttonText: this.loyaltyContent.metadata?.enrollmentSuccessActionText,
          clickToDismiss: true
        };

        events.emit('notification', notificationEventPayload);
      },

      signOutClickHandler() {
        const confirmationModalPayload = {
          ctaConfirmText: 'Yes, Sign Out',
          primaryText: 'Your order has already been assigned to this account. If you sign out now, you won\'t be able to sign in again until your next order and the account you are currently signed in with will receive loyalty credit.',
          onConfirm: () => {
            this.$store.dispatch('session/signOut');
          },
          title: 'Are you sure?'
        };

        this.$openModal('confirmation', confirmationModalPayload);
      },

      loyaltyProgramDetailsClickHandler() {
        this.$openModal('loyaltyProgramDetailsModal');
      },

      loyaltyTermsAndConditionsClickHandler() {
        this.$openModal('loyaltyTermsAndConditionsModal');
      }
    }
  };
</script>
